import { useTranslation } from 'react-i18next';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { BasicButton } from '@components/common/BasicButton';
import { QuickLinksPanel } from '@layouts/QuickLinks';
import { ContactsPanel } from '@layouts/ContactsPanel';
import { useContactLinks } from '@/hooks/useContactLinks';
import type { QuickLink } from '@store/models/booking';
import { useHelpDrawerStyles } from './HelpDrawer.styles';

export interface HelpDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quickLinks: QuickLink[];
  /** Optional: invoked once per quick-link click. Wired by the Header for telemetry. */
  onLinkClick?: (link: QuickLink) => void;
}

export function HelpDrawer({
  open,
  onOpenChange,
  quickLinks,
  onLinkClick,
}: HelpDrawerProps): React.JSX.Element {
  const { t } = useTranslation('header');
  const styles = useHelpDrawerStyles();
  const contacts = useContactLinks();

  const hasQuickLinks = quickLinks.length > 0;
  const hasContacts = contacts.length > 0;

  return (
    <OverlayDrawer
      open={open}
      onOpenChange={(_, data) => onOpenChange(data.open)}
      position="end"
      size="small"
      data-testid="help-drawer"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <BasicButton
              variant="subtle"
              aria-label={t('common.close', { defaultValue: 'Close' })}
              icon={<Dismiss24Regular />}
              onClick={() => onOpenChange(false)}
              data-testid="help-drawer-close"
            />
          }
        >
          {t('help.label')}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        {hasQuickLinks && <QuickLinksPanel links={quickLinks} onLinkClick={onLinkClick} />}
        {hasContacts && <ContactsPanel contacts={contacts} />}
        {!hasQuickLinks && !hasContacts && <Text className={styles.empty}>{t('help.empty')}</Text>}
      </DrawerBody>
    </OverlayDrawer>
  );
}

HelpDrawer.displayName = 'HelpDrawer';
